import { HotkeyDisplay } from '@app/core/hotkeys/HotkeyDisplay';
import { useAuditMention } from '@app/modules/audit/modules/station-audit/hooks/useAuditMention';
import { AuditLiveMention } from '@app/modules/audit/modules/station-audit/types';
import { AuditLiveMentionConfidence } from '@mi-front/core';
import {
  ThumbDownOutlined as ThumbDownOutlinedIcon,
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
} from '@mui/icons-material';
import { Button, DialogActions } from '@mui/material';
import { useCallback } from 'react';
import { defineMessage, FormattedMessage } from 'react-intl';
import { useMentionConfidenceActions } from '../mentions/useMentionConfidenceActions';
import { useVerifyMatchMentionKeyboard } from './useVerifyMatchMentionKeyboard';

const ACCEPT_HOTKEY = 'a';
const REJECT_HOTKEY = 'r';

const ACCEPT_MENTION_MESSAGE = defineMessage({
  id: 'audit.verifyMatch.accept',
  defaultMessage: 'Aceptar',
});

const REJECT_MENTION_MESSAGE = defineMessage({
  id: 'audit.verifyMatch.reject',
  defaultMessage: 'Rechazar',
});

export interface VerifyMatchMentionConfidenceProps {
  stationId: string;
  mention: AuditLiveMention;
  hotkeysEnabled: boolean;
  onConfidenceChange?: (mentionId: string, confidence: AuditLiveMentionConfidence) => void;
}

/**
 * Footer accept/reject actions for a mention in the verify-match dialog.
 */
export function VerifyMatchMentionConfidence({
  stationId,
  mention,
  hotkeysEnabled,
  onConfidenceChange,
}: VerifyMatchMentionConfidenceProps) {
  const { data, confidenceMutation } = useAuditMention(stationId, mention.id);
  const confidence = data?.confidence ?? mention.confidence ?? AuditLiveMentionConfidence.AUTO;
  const mutateConfidence = confidenceMutation.mutate;
  const isAccepted = confidence === AuditLiveMentionConfidence.ACCEPTED;

  const handleConfidenceChange = useCallback(
    (next: AuditLiveMentionConfidence) => {
      mutateConfidence(next, {
        onSuccess: () => {
          onConfidenceChange?.(mention.id, next);
        },
      });
    },
    [mutateConfidence, mention.id, onConfidenceChange],
  );

  const { onAccept, onReject, canChange } = useMentionConfidenceActions({
    confidence,
    onConfidenceChange: handleConfidenceChange,
  });

  useVerifyMatchMentionKeyboard(hotkeysEnabled && canChange, { onAccept, onReject });

  if (!canChange) {
    return null;
  }

  return (
    <DialogActions sx={{ px: 3, py: 1.5 }}>
      <Button
        color="error"
        variant="outlined"
        startIcon={<ThumbDownOutlinedIcon />}
        onClick={onReject}
      >
        <FormattedMessage {...REJECT_MENTION_MESSAGE} />
        &nbsp;&nbsp;
        <HotkeyDisplay hotkey={REJECT_HOTKEY} />
      </Button>
      <Button
        color="success"
        variant={isAccepted ? 'contained' : 'outlined'}
        startIcon={isAccepted ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
        onClick={onAccept}
      >
        <FormattedMessage {...ACCEPT_MENTION_MESSAGE} />
        &nbsp;&nbsp;
        <HotkeyDisplay hotkey={ACCEPT_HOTKEY} />
      </Button>
    </DialogActions>
  );
}
